/**
 * Line configurator — where equipment has to sit, and what the line can do.
 *
 * Everything here is arithmetic on a moving belt. No heuristics, no lookup
 * tables, no opinions about brands: distance is speed times time, and the times
 * are the ones actually measured on this installation.
 *
 * The constraint people get wrong is the first one below. A verdict is not
 * instant — inspection takes hundreds of milliseconds on a CPU — and the part
 * keeps moving the whole time. Put the rejector too close to the camera and it
 * fires at empty belt, every time, with nothing in the logs to say why.
 *
 * Shared so the UI and the backend cannot disagree: a configurator that says a
 * layout is fine while the runtime thinks otherwise is worse than no
 * configurator.
 */
/** Time in ms for the belt to travel a distance. */
const travelMs = (mm, speedMmS) => (mm / speedMmS) * 1000;
/** Distance in mm the belt covers in a time. */
const travelMm = (ms, speedMmS) => (ms / 1000) * speedMmS;
export function analyseLine(input, devices = []) {
    const { speedMmS, partLengthMm, gapMm, inspectionMs } = input;
    const worstMs = input.inspectionWorstMs ?? inspectionMs;
    const pitchMm = partLengthMm + gapMm;
    const partIntervalMs = travelMs(pitchMm, speedMmS);
    const throughputPerMin = pitchMm > 0 ? (speedMmS * 60) / pitchMm : 0;
    const travelDuringInspectionMm = travelMm(inspectionMs, speedMmS);
    const findings = [];
    // The line cannot present parts faster than they can be judged. This is the
    // ceiling everything else sits under.
    if (partIntervalMs < worstMs) {
        findings.push({
            severity: 'error',
            message: `Parts arrive every ${partIntervalMs.toFixed(0)} ms but a slow inspection takes ` +
                `${worstMs.toFixed(0)} ms — the line outruns the camera and parts go by unchecked.`,
            fix: `Slow the belt to ${((pitchMm / worstMs) * 1000).toFixed(0)} mm/s, widen the gap to ` +
                `${(travelMm(worstMs, speedMmS) - partLengthMm).toFixed(0)} mm, or inspect on faster hardware.`,
        });
    }
    else if (partIntervalMs < worstMs * 1.5) {
        findings.push({
            severity: 'warning',
            message: `Only ${(partIntervalMs - worstMs).toFixed(0)} ms of slack between a slow inspection and ` +
                `the next part. One hiccup and a part passes unjudged.`,
            fix: 'Aim for at least 50% headroom over the worst-case inspection time.',
        });
    }
    const placements = devices.map((d) => {
        const f = [];
        const actuation = d.actuationMs ?? 0;
        if (d.role === 'rejector') {
            // The verdict has to exist AND the actuator has to have moved before the
            // part arrives. Both delays add.
            const needed = travelMm(inspectionMs + actuation, speedMmS);
            const neededWorst = travelMm(worstMs + actuation, speedMmS);
            if (d.positionMm === null) {
                f.push({
                    severity: 'error',
                    message: 'No position set, so its timing cannot be computed at all.',
                    fix: `Measure the distance from the camera. It must be at least ${neededWorst.toFixed(0)} mm.`,
                });
            }
            else if (d.positionMm < needed) {
                f.push({
                    severity: 'error',
                    message: `At ${d.positionMm} mm it fires ${(needed - d.positionMm).toFixed(0)} mm too late — ` +
                        `the part is already past. It will hit empty belt every time.`,
                    fix: `Move it to at least ${neededWorst.toFixed(0)} mm from the camera.`,
                });
            }
            else if (d.positionMm < neededWorst) {
                f.push({
                    severity: 'warning',
                    message: `${d.positionMm} mm is enough for a typical inspection but not for a slow one ` +
                        `(${neededWorst.toFixed(0)} mm needed).`,
                    fix: `Move it back ${(neededWorst - d.positionMm).toFixed(0)} mm for margin.`,
                });
            }
            if (d.actuationMs === null) {
                f.push({
                    severity: 'error',
                    message: 'Actuation delay unknown, so the fire moment is a guess.',
                    fix: 'Measure it: trigger the device and time until the part actually moves.',
                });
            }
            // A dwell longer than the gap catches the part behind it as well.
            const gapTimeMs = travelMs(gapMm, speedMmS);
            if (d.dwellMs !== null && gapMm > 0 && d.dwellMs > gapTimeMs) {
                f.push({
                    severity: 'warning',
                    message: `It stays active ${d.dwellMs} ms but the next part arrives ${gapTimeMs.toFixed(0)} ms ` +
                        `later — a good part behind a rejected one gets pushed out too.`,
                    fix: `Shorten the dwell below ${gapTimeMs.toFixed(0)} ms, or widen the gap.`,
                });
            }
        }
        if (d.role === 'trigger' && d.positionMm !== null && d.positionMm > 0) {
            f.push({
                severity: 'info',
                message: `Sits ${d.positionMm} mm before the camera — that head start is free inspection time.`,
            });
        }
        return { ...d, findings: f };
    });
    const rejectors = placements.filter((p) => p.role === 'rejector');
    if (rejectors.length === 0) {
        findings.push({
            severity: 'warning',
            message: 'No rejector configured — the line can judge parts but not act on the verdict.',
        });
    }
    if (!devices.some((d) => d.role === 'trigger')) {
        findings.push({
            severity: 'warning',
            message: 'No trigger configured, so inspections are not tied to a part arriving.',
            fix: 'Add a photocell, or accept free-running capture and its duplicate inspections.',
        });
    }
    // An encoder is what makes timing survive a speed change. Without one, every
    // distance above is only true at exactly this speed.
    let encoderErrorMm = null;
    if (input.mmPerPulse) {
        encoderErrorMm = input.mmPerPulse;
        if (input.mmPerPulse > partLengthMm / 4) {
            findings.push({
                severity: 'warning',
                message: `One encoder pulse is ${input.mmPerPulse} mm — a quarter of a ${partLengthMm} mm part ` +
                    `or more. Reject timing cannot be finer than one pulse.`,
                fix: `Use an encoder with at least ${Math.ceil(partLengthMm / 4)} mm resolution or better.`,
            });
        }
    }
    else if (!devices.some((d) => d.role === 'encoder')) {
        findings.push({
            severity: 'warning',
            message: 'No encoder, so timing is computed from the clock and assumes the belt never changes speed.',
            fix: 'Add an encoder if the belt is ever slowed, or accept mistimed rejects when it is.',
        });
    }
    return {
        throughputPerMin,
        partIntervalMs,
        travelDuringInspectionMm,
        minRejectDistanceMm: travelMm(inspectionMs, speedMmS),
        minRejectDistanceWorstMm: travelMm(worstMs, speedMmS),
        encoderErrorMm,
        findings,
        placements,
    };
}
/**
 * Which kind of actuator suits this line.
 *
 * Advice, not a decision: the time window and the part's mass rule out some
 * options outright, and the rest is a judgement about the product that belongs
 * to whoever is standing at the machine.
 */
export function suggestRejector(input) {
    const windowMs = travelMs(input.partLengthMm, input.speedMmS);
    const out = [];
    if (windowMs < 40) {
        out.push({
            severity: 'info',
            message: `A part is in front of the actuator for only ${windowMs.toFixed(0)} ms. Only an air ` +
                `blast is fast enough — a cylinder cannot extend and retract in that time.`,
        });
    }
    else if (windowMs < 150) {
        out.push({
            severity: 'info',
            message: `${windowMs.toFixed(0)} ms in front of the actuator: an air blast is comfortable, ` +
                `a fast pusher is possible.`,
        });
    }
    else {
        out.push({
            severity: 'info',
            message: `${windowMs.toFixed(0)} ms in front of the actuator — long enough for a pusher or a ` +
                `diverter gate, which handle heavier parts than air can move.`,
        });
    }
    if (input.gapMm < input.partLengthMm * 0.5) {
        out.push({
            severity: 'warning',
            message: `Parts are close together (${input.gapMm} mm apart). A diverter that holds position will ` +
                `take the following part with it.`,
            fix: 'Prefer a pulsed actuator, or widen the spacing.',
        });
    }
    return out;
}
//# sourceMappingURL=lineConfig.js.map